/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import FormModal from '../../components/FormModal';
import { RowMenu } from '../../components/SettingGroups';
import { dayLabel, money, useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';

const PROMOTION_FIELDS = [
  { name: 'code', label: 'รหัส', required: true },
  { name: 'nameEn', label: 'ชื่อ (อังกฤษ)', required: true },
  { name: 'nameTh', label: 'ชื่อ (ไทย)' },
  { name: 'discountType', label: 'ประเภทส่วนลด', type: 'select', required: true,
    options: [{ value: 'percent', label: 'เปอร์เซ็นต์ (%)' }, { value: 'amount', label: 'จำนวนเงิน' }] },
  { name: 'discountValue', label: 'ส่วนลด', type: 'number', required: true },
  { name: 'startsOn', label: 'เริ่มวันที่', type: 'date' },
  { name: 'endsOn', label: 'ถึงวันที่', type: 'date' },
];

const payloadOf = (v) => ({
  code: v.code,
  nameEn: v.nameEn,
  nameTh: v.nameTh || null,
  discountType: v.discountType,
  discountValue: Number(v.discountValue) || 0,
  startsOn: v.startsOn || null,
  endsOn: v.endsOn || null,
});

/**
 * starts_on and ends_on are DATE columns — a calendar day, never an instant —
 * so they are anchored rather than shifted into a zone, which is what dayLabel
 * does. The slice keeps this right if the column ever arrives as a timestamp.
 */
const day = (value) => (value
  ? dayLabel(String(value).slice(0, 10), 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : 'ไม่กำหนด');

export function SettingPromotion() {
  const promotions = useLoader(() => settingsApi.promotions(), []);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <SettingsPage action="สร้างโปรโมชั่น" onAction={() => setCreating(true)}>
      <Panel loader={promotions} empty={(d) => !d.promotions.length} emptyIcon="promotion">
        {(d) => (
          /* Divs rather than a <table>: in the original the header sits on the
             page background and each record is its own white band, which is one
             set of box properties on a div but a per-cell corner-and-shadow hack
             on table rows. The roles hand the table back to a screen reader.
             --band-cols is declared once so the header and its rows cannot
             drift apart. */
          <div
            className="band-table"
            role="table"
            aria-label="โปรโมชั่น"
            style={{ '--band-cols': 'minmax(0, 2.2fr) 1fr 1.8fr 1fr 40px' }}
          >
            <div className="band-head" role="row">
              <span role="columnheader">ชื่อ</span>
              <span role="columnheader">ราคาลด</span>
              <span role="columnheader">วันที่</span>
              <span role="columnheader">สถานะ</span>
              <span role="columnheader" />
            </div>

            {d.promotions.map((p) => (
              <div className="band-row" role="row" key={p.id}>
                <span role="cell" className="band-cell promo-name">
                  {/* The capture shows this mark on every row, not only the
                      percentage ones, so it is the page's icon for a promotion
                      rather than a per-row type indicator. */}
                  <span className="promo-mark" aria-hidden="true">%</span>
                  <span>{p.name_th || p.name_en}</span>
                </span>
                <span role="cell" className="band-cell">
                  {p.discount_type === 'percent' ? `${p.discount_value} %` : money(p.discount_value)}
                </span>
                <span role="cell" className="band-cell">
                  {p.starts_on || p.ends_on
                    ? `เริ่ม ${day(p.starts_on)} ถึง ${day(p.ends_on)}`
                    : 'ไม่กำหนด'}
                </span>
                {/* A pill here, unlike แพ็กเกจ, where สถานะ is plain text. */}
                <span role="cell" className="band-cell">
                  <span className={`badge ${p.is_active ? 'badge-green' : 'badge-grey'}`}>
                    {p.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                  </span>
                </span>
                <span role="cell" className="band-menu">
                  {/* DELETE retires the row rather than erasing it, so the menu
                      says so and offers the way back. */}
                  <RowMenu
                    items={[
                      { label: 'แก้ไข', onClick: () => setEditing(p) },
                      p.is_active
                        ? { label: 'ปิดใช้งาน',
                            danger: true,
                            onClick: async () => {
                              await settingsApi.remove('promotions', p.id);
                              promotions.reload();
                            } }
                        : { label: 'เปิดใช้งาน',
                            onClick: async () => {
                              await settingsApi.restore('promotions', p.id);
                              promotions.reload();
                            } },
                    ]}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {creating && (
        <FormModal
          title="สร้างโปรโมชั่น"
          fields={PROMOTION_FIELDS}
          onSubmit={async (v) => {
            await settingsApi.createPromotion(payloadOf(v));
            promotions.reload();
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <FormModal
          title={`แก้ไขโปรโมชั่น — ${editing.name_th || editing.name_en}`}
          fields={PROMOTION_FIELDS}
          initial={{
            code: editing.code,
            nameEn: editing.name_en,
            nameTh: editing.name_th || '',
            discountType: editing.discount_type,
            discountValue: editing.discount_value,
            // <input type="date"> only accepts YYYY-MM-DD, which is what the
            // DATE column already returns.
            startsOn: editing.starts_on || '',
            endsOn: editing.ends_on || '',
          }}
          onSubmit={async (v) => {
            await settingsApi.update('promotions', editing.id, payloadOf(v));
            promotions.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </SettingsPage>
  );
}
